import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { onWatchSignal, useSendWatchSignal, type WatchSignalEvent } from "@/lib/realtime";
import { useAuth } from "@/lib/auth-context";
import { decryptMediaForConversation } from "@/lib/e2ee";
import { mediaUrl } from "@/lib/api";
import { bytesToBlob } from "@/lib/utils";
import { toast } from "sonner";
import { Play, X, Loader2 } from "lucide-react";

export type WatchStatus = "idle" | "inviting" | "active";

export interface WatchSession {
  conversationId: string;
  peerUserId: string;
  peerDisplayName: string;
  /** The video message's own media_url - each side decrypts their own local
   * copy from it (see decryptAndPlay below), same as EncryptedVideo already
   * does for a normal video-reply bubble. The server never sees the video
   * itself, only opaque play/pause/seek signaling. */
  mediaUrl: string;
  messageId: string;
  keyEpoch: number;
}

interface IncomingWatchInvite {
  conversationId: string;
  peerUserId: string;
  peerDisplayName: string;
  mediaUrl: string;
  messageId: string;
  keyEpoch: number;
}

interface SyncPayload {
  action: "play" | "pause" | "seek";
  positionSeconds: number;
}

interface WatchContextValue {
  status: WatchStatus;
  session: WatchSession | null;
  invite: (conversationId: string, peerUserId: string, peerDisplayName: string, mediaUrl: string, messageId: string, keyEpoch: number) => void;
  end: () => void;
  sendSync: (action: "play" | "pause" | "seek", positionSeconds: number) => void;
  /** Bumped (via a monotonically increasing seq) every time a sync event
   * arrives from the peer - WatchTogetherPlayer watches this to apply the
   * remote action to its own <video> element. */
  lastRemoteSync: { action: "play" | "pause" | "seek"; positionSeconds: number; seq: number } | null;
}

const WatchContext = createContext<WatchContextValue | null>(null);

export function WatchTogetherProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const sendWatchSignal = useSendWatchSignal();
  const [status, setStatus] = useState<WatchStatus>("idle");
  const [session, setSession] = useState<WatchSession | null>(null);
  const [incomingInvite, setIncomingInvite] = useState<IncomingWatchInvite | null>(null);
  const [lastRemoteSync, setLastRemoteSync] = useState<WatchContextValue["lastRemoteSync"]>(null);
  const sessionRef = useRef<WatchSession | null>(null);
  const seqRef = useRef(0);
  sessionRef.current = session;

  useEffect(() => {
    return onWatchSignal((event: WatchSignalEvent) => {
      const current = sessionRef.current;

      if (event.type === "watch:invite") {
        const payload = event.payload as { mediaUrl: string; messageId: string; fromDisplayName: string; keyEpoch: number };
        setIncomingInvite({
          conversationId: event.conversationId,
          peerUserId: event.fromUserId,
          peerDisplayName: payload.fromDisplayName,
          mediaUrl: payload.mediaUrl,
          messageId: payload.messageId,
          keyEpoch: payload.keyEpoch
        });
        toast(`${payload.fromDisplayName} wants to watch together`);
        return;
      }

      if (!current || current.conversationId !== event.conversationId) return;

      if (event.type === "watch:accept") {
        setStatus("active");
      } else if (event.type === "watch:decline") {
        toast.error(`${current.peerDisplayName} declined watching together`);
        setStatus("idle");
        setSession(null);
      } else if (event.type === "watch:sync") {
        const payload = event.payload as SyncPayload;
        seqRef.current += 1;
        setLastRemoteSync({ action: payload.action, positionSeconds: payload.positionSeconds, seq: seqRef.current });
      } else if (event.type === "watch:end") {
        toast(`${current.peerDisplayName} ended watching together`);
        setStatus("idle");
        setSession(null);
      }
    });
  }, []);

  function invite(conversationId: string, peerUserId: string, peerDisplayName: string, videoUrl: string, messageId: string, keyEpoch: number) {
    if (session) return; // one session at a time, same as calling
    const newSession = { conversationId, peerUserId, peerDisplayName, mediaUrl: videoUrl, messageId, keyEpoch };
    setSession(newSession);
    setStatus("inviting");
    sendWatchSignal({
      type: "watch:invite",
      toUserId: peerUserId,
      conversationId,
      payload: { mediaUrl: videoUrl, messageId, keyEpoch, fromDisplayName: user?.display_name ?? "Someone" }
    });
  }

  function acceptInvite() {
    if (!incomingInvite) return;
    setSession(incomingInvite);
    setStatus("active");
    sendWatchSignal({ type: "watch:accept", toUserId: incomingInvite.peerUserId, conversationId: incomingInvite.conversationId, payload: {} });
    setIncomingInvite(null);
  }

  function declineInvite() {
    if (!incomingInvite) return;
    sendWatchSignal({ type: "watch:decline", toUserId: incomingInvite.peerUserId, conversationId: incomingInvite.conversationId, payload: {} });
    setIncomingInvite(null);
  }

  function end() {
    if (session) sendWatchSignal({ type: "watch:end", toUserId: session.peerUserId, conversationId: session.conversationId, payload: {} });
    setStatus("idle");
    setSession(null);
  }

  function sendSync(action: "play" | "pause" | "seek", positionSeconds: number) {
    if (!session) return;
    sendWatchSignal({ type: "watch:sync", toUserId: session.peerUserId, conversationId: session.conversationId, payload: { action, positionSeconds } });
  }

  return (
    <WatchContext.Provider value={{ status, session, invite, end, sendSync, lastRemoteSync }}>
      {children}
      {incomingInvite && (
        <div className="fixed inset-x-3 bottom-20 z-50 mx-auto flex max-w-sm items-center gap-3 rounded-xl bg-popover p-3 shadow-xl">
          <div className="min-w-0 flex-1 text-sm">
            <span className="font-medium">{incomingInvite.peerDisplayName}</span> wants to watch together
          </div>
          <button onClick={declineInvite} className="rounded-full bg-secondary p-2">
            <X className="h-4 w-4" />
          </button>
          <button onClick={acceptInvite} className="rounded-full bg-foreground p-2 text-background">
            <Play className="h-4 w-4" />
          </button>
        </div>
      )}
      {status === "active" && session && <WatchTogetherPlayer session={session} onEnd={end} lastRemoteSync={lastRemoteSync} onLocalSync={sendSync} />}
    </WatchContext.Provider>
  );
}

/** Full-screen synced player. Each side independently decrypts its OWN local
 * copy of the same message's video (identical mechanism to EncryptedVideo,
 * just with an imperative <video> ref instead of plain `controls`, since
 * play/pause/seek need to be driven both by the person's own taps AND by
 * incoming remote sync events). */
function WatchTogetherPlayer({
  session,
  onEnd,
  lastRemoteSync,
  onLocalSync
}: {
  session: WatchSession;
  onEnd: () => void;
  lastRemoteSync: WatchContextValue["lastRemoteSync"];
  onLocalSync: (action: "play" | "pause" | "seek", positionSeconds: number) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [playing, setPlaying] = useState(false);
  // Set while applying a REMOTE sync event to the local <video>, so the
  // native play/pause/seeked events that fire as a result don't get
  // re-broadcast right back to the peer as if they were a local action -
  // that would echo forever between the two sides.
  const applyingRemoteRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;
    (async () => {
      try {
        const url = mediaUrl(session.mediaUrl);
        if (!url) throw new Error("no media url");
        const res = await fetch(url);
        if (!res.ok) throw new Error("fetch failed");
        const encryptedBytes = new Uint8Array(await res.arrayBuffer());
        const decryptedBytes = await decryptMediaForConversation(session.conversationId, encryptedBytes, session.keyEpoch);
        const blob = bytesToBlob(decryptedBytes, "video/webm");
        createdUrl = URL.createObjectURL(blob);
        if (!cancelled) setObjectUrl(createdUrl);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [session.conversationId, session.mediaUrl]);

  // Apply an incoming remote sync event to the local video.
  useEffect(() => {
    if (!lastRemoteSync || !videoRef.current) return;
    const video = videoRef.current;
    applyingRemoteRef.current = true;
    if (Math.abs(video.currentTime - lastRemoteSync.positionSeconds) > 0.75) {
      video.currentTime = lastRemoteSync.positionSeconds;
    }
    if (lastRemoteSync.action === "play") video.play().catch(() => {});
    else if (lastRemoteSync.action === "pause") video.pause();
    // Let the resulting native events (which do fire synchronously/soon
    // after) pass through the guard before clearing it.
    setTimeout(() => {
      applyingRemoteRef.current = false;
    }, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastRemoteSync?.seq]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/95">
      <div className="absolute left-0 right-0 top-0 flex items-center justify-between p-4 text-white">
        <span className="text-sm">Watching together with {session.peerDisplayName}</span>
        <button onClick={onEnd} className="rounded-full bg-white/10 p-2">
          <X className="h-4 w-4" />
        </button>
      </div>

      {error ? (
        <p className="text-sm text-white/70">Couldn't load this video</p>
      ) : !objectUrl ? (
        <Loader2 className="h-8 w-8 animate-spin text-white/70" />
      ) : (
        <div className="relative w-full max-w-lg px-4">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            ref={videoRef}
            src={objectUrl}
            playsInline
            className="w-full rounded-lg"
            onPlay={() => {
              setPlaying(true);
              if (!applyingRemoteRef.current) onLocalSync("play", videoRef.current?.currentTime ?? 0);
            }}
            onPause={() => {
              setPlaying(false);
              if (!applyingRemoteRef.current) onLocalSync("pause", videoRef.current?.currentTime ?? 0);
            }}
            onSeeked={() => {
              if (!applyingRemoteRef.current) onLocalSync("seek", videoRef.current?.currentTime ?? 0);
            }}
          />
          <button
            onClick={() => (playing ? videoRef.current?.pause() : videoRef.current?.play())}
            className="absolute inset-0 flex items-center justify-center"
          >
            {!playing && <Play className="h-14 w-14 fill-white/90 text-white/90" />}
          </button>
        </div>
      )}
    </div>
  );
}

export function useWatchTogether() {
  const ctx = useContext(WatchContext);
  if (!ctx) throw new Error("useWatchTogether must be used within WatchTogetherProvider");
  return ctx;
}
